import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { useSession } from '../auth/session.tsx';

/** The visit itinerary's reads (api/src/routes/itinerary.ts). Nothing here is stored; every list writes an audit row (141). */

export interface ItineraryFacilityOption {
  id: string; facility_name: string; street_address: string | null; town: string | null; due: number;
  /** Residents there who are not due by the date (A-#54): shown on the map's hover only. */
  up_to_date: number;
  /** The building's own coordinates, for the map view; numeric, so they arrive as strings, and null where none. */
  latitude: number | string | null; longitude: number | string | null;
}
export interface ItineraryClient {
  client_id: string; name: string; room: string | null; visit_type: string; due_on: string; overdue: boolean;
}
/** A resident not due by the date (A-#54): listed and printed only when the stop is set to see everyone. */
export interface ItineraryResident { client_id: string; name: string; room: string | null; last_visit: string | null }
export interface ItineraryFacilityDetail {
  date: string;
  facility: { id: string; facility_name: string; street_address: string | null; city: string | null; state: string | null; zip: string | null; phone: string | null };
  away: number;
  away_up_to_date: number;
  /** The clients due, as before. */
  clients: ItineraryClient[];
  /** Every other resident, up to date. */
  up_to_date: ItineraryResident[];
}

export function useItineraryAllowed() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['visit-itinerary', 'allowed'],
    queryFn: () => apiFetch<{ allowed: boolean }>('/visit-itinerary/allowed', getToken),
    staleTime: 5 * 60_000,
  });
}

export function useItineraryStaff(enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['visit-itinerary', 'staff'],
    queryFn: () => apiFetch<{ staff: { id: string; full_name: string }[] }>('/visit-itinerary/staff', getToken),
    enabled,
  });
}

/** Buildings with clients due by the date. The date is the only thing in the address, and it is a date. */
export function useItineraryFacilities(date: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['visit-itinerary', 'facilities', date],
    queryFn: () => apiFetch<{ date: string; facilities: ItineraryFacilityOption[] }>(`/visit-itinerary/facilities?date=${date}`, getToken),
    enabled: enabled && /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
}

export function fetchItineraryFacility(getToken: () => Promise<string>, id: string, date: string) {
  return apiFetch<ItineraryFacilityDetail>(`/visit-itinerary/facilities/${id}?date=${date}`, getToken);
}
